import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, Search, ChevronRight,
  Car, Home as HomeIcon, Briefcase, Sofa, Armchair, Users, 
  Key, Building, Bed, Package, Sparkles
} from 'lucide-react';

const categoryIcons = {
  'motors': Car,
  'property-rent': Key,
  'property-sale': HomeIcon,
  'off-plan': Building,
  'rooms': Bed,
  'jobs': Briefcase,
  'classifieds': Sofa,
  'furniture': Armchair,
  'community': Users,
};

// Removed per-category colors — using unified dark/red theme

// Complete category structure
const categoryData = {
  'motors': {
    name: 'Motors',
    nameAr: 'السيارات',
    subcategories: [
      { id: 'used-cars', name: 'Used Cars', nameAr: 'سيارات مستعملة', hasChildren: true },
      { id: 'new-cars', name: 'New Cars', nameAr: 'سيارات جديدة', hasChildren: true },
      { id: 'export-cars', name: 'Export Cars', nameAr: 'سيارات للتصدير', hasChildren: true },
      { id: 'rental-cars', name: 'Rental Cars', nameAr: 'سيارات للإيجار', hasChildren: true },
      { id: 'motorcycles', name: 'Motorcycles', nameAr: 'دراجات نارية', hasChildren: true },
      { id: 'auto-accessories', name: 'Auto Accessories & Parts', nameAr: 'إكسسوارات وقطع غيار', hasChildren: true },
      { id: 'heavy-vehicles', name: 'Heavy Vehicles', nameAr: 'مركبات ثقيلة', hasChildren: true },
      { id: 'boats', name: 'Boats', nameAr: 'قوارب', hasChildren: true },
      { id: 'number-plates', name: 'Number Plates', nameAr: 'لوحات أرقام' },
    ]
  },
  'property-rent': {
    name: 'Property for Rent',
    nameAr: 'عقارات للإيجار',
    subcategories: [
      { id: 'apartments-rent', name: 'Apartments', nameAr: 'شقق' },
      { id: 'villas-rent', name: 'Villas', nameAr: 'فلل' },
      { id: 'townhouses-rent', name: 'Townhouses', nameAr: 'تاون هاوس' },
      { id: 'penthouses-rent', name: 'Penthouses', nameAr: 'بنتهاوس' },
      { id: 'commercial-rent', name: 'Commercial', nameAr: 'تجاري' },
      { id: 'offices-rent', name: 'Offices', nameAr: 'مكاتب' },
      { id: 'shops-rent', name: 'Shops', nameAr: 'محلات' },
      { id: 'warehouses-rent', name: 'Warehouses', nameAr: 'مستودعات' },
    ]
  },
  'property-sale': {
    name: 'Property for Sale',
    nameAr: 'عقارات للبيع',
    subcategories: [
      { id: 'apartments-sale', name: 'Apartments', nameAr: 'شقق' },
      { id: 'villas-sale', name: 'Villas', nameAr: 'فلل' },
      { id: 'townhouses-sale', name: 'Townhouses', nameAr: 'تاون هاوس' },
      { id: 'penthouses-sale', name: 'Penthouses', nameAr: 'بنتهاوس' },
      { id: 'land-sale', name: 'Land', nameAr: 'أراضي' },
      { id: 'commercial-sale', name: 'Commercial', nameAr: 'تجاري' },
      { id: 'buildings-sale', name: 'Buildings', nameAr: 'مباني' },
    ]
  },
  'off-plan': {
    name: 'Off-Plan Properties',
    nameAr: 'عقارات على الخريطة',
    subcategories: [
      { id: 'apartments-offplan', name: 'Apartments', nameAr: 'شقق' },
      { id: 'villas-offplan', name: 'Villas', nameAr: 'فلل' },
      { id: 'townhouses-offplan', name: 'Townhouses', nameAr: 'تاون هاوس' },
    ]
  },
  'rooms': {
    name: 'Rooms for Rent',
    nameAr: 'غرف للإيجار',
    subcategories: [
      { id: 'rooms-male', name: 'For Males', nameAr: 'للذكور' },
      { id: 'rooms-female', name: 'For Females', nameAr: 'للإناث' },
      { id: 'rooms-family', name: 'For Family', nameAr: 'للعائلات' },
      { id: 'rooms-couples', name: 'For Couples', nameAr: 'للأزواج' },
      { id: 'bed-space', name: 'Bed Space', nameAr: 'سرير مشترك' },
    ]
  },
  'jobs': {
    name: 'Jobs',
    nameAr: 'وظائف',
    subcategories: [
      { id: 'accounting-finance', name: 'Accounting / Finance', nameAr: 'المحاسبة / المالية' },
      { id: 'automobile', name: 'Automobile', nameAr: 'السيارات' },
      { id: 'beauty-salon', name: 'Beauty / Salon', nameAr: 'التجميل / الصالونات' },
      { id: 'cleaner-housekeeper', name: 'Cleaner / Housekeeper', nameAr: 'التنظيف / التدبير المنزلي' },
      { id: 'construction', name: 'Construction', nameAr: 'البناء' },
      { id: 'cook-chef', name: 'Cook / Chef', nameAr: 'الطبخ / الشيف' },
      { id: 'customer-service', name: 'Customer Service / Call Centre', nameAr: 'خدمة العملاء / مركز الاتصال' },
      { id: 'data-management', name: 'Data Management & Analysis', nameAr: 'إدارة وتحليل البيانات' },
      { id: 'design', name: 'Design', nameAr: 'التصميم' },
      { id: 'driver-delivery', name: 'Driver / Delivery', nameAr: 'سائق / توصيل' },
      { id: 'education', name: 'Education', nameAr: 'التعليم' },
      { id: 'engineering', name: 'Engineering', nameAr: 'الهندسة' },
      { id: 'event-management', name: 'Event Management & Operations', nameAr: 'إدارة الفعاليات والعمليات' },
      { id: 'handyman-technician', name: 'Handyman / Technician', nameAr: 'فني / حرفي' },
      { id: 'hr-admin', name: 'HR / Admin', nameAr: 'الموارد البشرية / الإدارة' },
      { id: 'it-technology', name: 'Information Technology', nameAr: 'تقنية المعلومات' },
      { id: 'legal-services', name: 'Legal Services', nameAr: 'الخدمات القانونية' },
      { id: 'logistics-distribution', name: 'Logistics & Distribution', nameAr: 'اللوجستيات والتوزيع' },
      { id: 'manufacturing-warehouse', name: 'Manufacturing / Warehouse', nameAr: 'التصنيع / المستودعات' },
      { id: 'marine-captain-crew', name: 'Marine Captain / Crew', nameAr: 'قبطان / طاقم بحري' },
      { id: 'marketing-advertising', name: 'Marketing / Advertising', nameAr: 'التسويق / الإعلان' },
      { id: 'media-art-entertainment', name: 'Media, Art & Entertainment', nameAr: 'الإعلام والفنون والترفيه' },
      { id: 'medical-healthcare', name: 'Medical / Healthcare', nameAr: 'الطب / الرعاية الصحية' },
      { id: 'real-estate', name: 'Real Estate', nameAr: 'العقارات' },
      { id: 'restaurant-operations', name: 'Restaurant Operations', nameAr: 'عمليات المطاعم' },
      { id: 'sales-business-dev', name: 'Sales / Business Development', nameAr: 'المبيعات / تطوير الأعمال' },
      { id: 'secretarial-front-office', name: 'Secretarial / Front Office', nameAr: 'السكرتارية / الاستقبال' },
      { id: 'security-guard', name: 'Security / Guard', nameAr: 'الأمن / الحراسة' },
      { id: 'sports-fitness', name: 'Sports & Fitness', nameAr: 'الرياضة واللياقة' },
      { id: 'travel-hospitality', name: 'Travel & Hospitality', nameAr: 'السفر والضيافة' },
      { id: 'other-jobs', name: 'Others', nameAr: 'أخرى' },
    ]
  },
  'classifieds': {
    name: 'Classifieds',
    nameAr: 'إعلانات متنوعة',
    subcategories: [
      { id: 'electronics', name: 'Electronics', nameAr: 'إلكترونيات', hasChildren: true },
      { id: 'home-appliances', name: 'Home Appliances', nameAr: 'أجهزة منزلية', hasChildren: true },
      { id: 'fashion-beauty', name: 'Fashion & Beauty', nameAr: 'أزياء وجمال', hasChildren: true },
      { id: 'kids-babies', name: 'Kids & Babies', nameAr: 'أطفال ورضع', hasChildren: true },
      { id: 'pets', name: 'Pets', nameAr: 'حيوانات أليفة', hasChildren: true },
      { id: 'sports-fitness', name: 'Sports & Fitness', nameAr: 'رياضة ولياقة', hasChildren: true },
      { id: 'hobbies-music', name: 'Hobbies & Music', nameAr: 'هوايات وموسيقى', hasChildren: true },
      { id: 'business-industrial', name: 'Business & Industrial', nameAr: 'أعمال وصناعة', hasChildren: true },
    ]
  },
  'furniture': {
    name: 'Furniture & Garden',
    nameAr: 'أثاث وحدائق',
    subcategories: [
      { id: 'sofas', name: 'Sofas & Seating', nameAr: 'كنب وجلوس' },
      { id: 'beds', name: 'Beds & Mattresses', nameAr: 'أسرة ومراتب' },
      { id: 'dining-tables', name: 'Dining Tables', nameAr: 'طاولات طعام' },
      { id: 'cabinets', name: 'Cabinets & Storage', nameAr: 'خزائن وتخزين' },
      { id: 'outdoor-furniture', name: 'Outdoor Furniture', nameAr: 'أثاث خارجي' },
      { id: 'home-decor', name: 'Home Décor', nameAr: 'ديكور منزلي' },
      { id: 'lighting', name: 'Lighting', nameAr: 'إضاءة' },
      { id: 'rugs-carpets', name: 'Rugs & Carpets', nameAr: 'سجاد' },
    ]
  },
  'community': {
    name: 'Community',
    nameAr: 'المجتمع',
    subcategories: [
      { id: 'services', name: 'Services', nameAr: 'خدمات', hasChildren: true },
      { id: 'classes', name: 'Classes', nameAr: 'دورات', hasChildren: true },
      { id: 'events', name: 'Events', nameAr: 'فعاليات' },
      { id: 'freelancers', name: 'Freelancers', nameAr: 'مستقلين' },
      { id: 'car-lift', name: 'Car Lift', nameAr: 'مشاركة سيارة' },
      { id: 'volunteers', name: 'Volunteers', nameAr: 'متطوعين' },
      { id: 'lost-found', name: 'Lost & Found', nameAr: 'مفقودات' },
    ]
  }
};

const subSubcategories = {
  'electronics': [
    { id: 'mobile-phones', name: 'Mobile Phones', nameAr: 'هواتف' },
    { id: 'laptops', name: 'Laptops', nameAr: 'لابتوب' },
    { id: 'tablets', name: 'Tablets', nameAr: 'تابلت' },
    { id: 'cameras', name: 'Cameras', nameAr: 'كاميرات' },
    { id: 'gaming-consoles', name: 'Gaming Consoles', nameAr: 'ألعاب' },
    { id: 'tvs-audio', name: 'TVs & Audio', nameAr: 'تلفزيون وصوتيات' },
  ],
  'home-appliances': [
    { id: 'refrigerators', name: 'Refrigerators', nameAr: 'ثلاجات' },
    { id: 'washing-machines', name: 'Washing Machines', nameAr: 'غسالات' },
    { id: 'air-conditioners', name: 'Air Conditioners', nameAr: 'مكيفات' },
    { id: 'microwaves', name: 'Microwaves', nameAr: 'ميكروويف' },
  ],
  'fashion-beauty': [
    { id: 'mens-clothing', name: "Men's Clothing", nameAr: 'ملابس رجالية' },
    { id: 'womens-clothing', name: "Women's Clothing", nameAr: 'ملابس نسائية' },
    { id: 'shoes', name: 'Shoes', nameAr: 'أحذية' },
    { id: 'watches', name: 'Watches', nameAr: 'ساعات' },
    { id: 'perfumes', name: 'Perfumes', nameAr: 'عطور' },
  ],
  'kids-babies': [
    { id: 'baby-gear', name: 'Baby Gear', nameAr: 'مستلزمات أطفال' },
    { id: 'toys', name: 'Toys', nameAr: 'ألعاب' },
    { id: 'strollers', name: 'Strollers', nameAr: 'عربات' },
    { id: 'kids-clothing', name: 'Clothing', nameAr: 'ملابس' },
  ],
  'pets': [
    { id: 'dogs', name: 'Dogs', nameAr: 'كلاب' },
    { id: 'cats', name: 'Cats', nameAr: 'قطط' },
    { id: 'birds', name: 'Birds', nameAr: 'طيور' },
    { id: 'fish', name: 'Fish', nameAr: 'أسماك' },
    { id: 'pet-accessories', name: 'Pet Accessories', nameAr: 'مستلزمات حيوانات' },
  ],
  'sports-fitness': [
    { id: 'gym-equipment', name: 'Gym Equipment', nameAr: 'أجهزة رياضية' },
    { id: 'bicycles', name: 'Bicycles', nameAr: 'دراجات' },
    { id: 'outdoor-sports', name: 'Outdoor Sports', nameAr: 'رياضات خارجية' },
  ],
  'hobbies-music': [
    { id: 'musical-instruments', name: 'Musical Instruments', nameAr: 'آلات موسيقية' },
    { id: 'books', name: 'Books', nameAr: 'كتب' },
    { id: 'collectibles', name: 'Collectibles', nameAr: 'مقتنيات' },
  ],
  'business-industrial': [
    { id: 'industrial-equipment', name: 'Industrial Equipment', nameAr: 'معدات صناعية' },
    { id: 'shop-equipment', name: 'Shop Equipment', nameAr: 'معدات محلات' },
    { id: 'agriculture', name: 'Agriculture', nameAr: 'زراعة' },
  ],
  'services': [
    { id: 'cleaning', name: 'Cleaning', nameAr: 'تنظيف' },
    { id: 'moving', name: 'Moving', nameAr: 'نقل' },
    { id: 'repair', name: 'Repair', nameAr: 'إصلاح' },
    { id: 'tutoring', name: 'Tutoring', nameAr: 'دروس خصوصية' },
  ],
  'classes': [
    { id: 'language', name: 'Language', nameAr: 'لغات' },
    { id: 'music-classes', name: 'Music', nameAr: 'موسيقى' },
    { id: 'fitness-classes', name: 'Fitness', nameAr: 'لياقة' },
  ],
  'motorcycles': [
    { id: 'adventure-touring', name: 'Adventure / Touring', nameAr: 'مغامرات / جولات' },
    { id: 'cafe-racer', name: 'Cafe Racer', nameAr: 'كافيه ريسر' },
    { id: 'cruiser-chopper', name: 'Cruiser / Chopper', nameAr: 'كروزر / تشوبر' },
    { id: 'golf-cart', name: 'Golf Cart', nameAr: 'عربة غولف' },
    { id: 'karting', name: 'Karting', nameAr: 'كارتينغ' },
    { id: 'mo-ped', name: 'Mo-Ped', nameAr: 'موبد' },
    { id: 'off-road', name: 'Off-Road', nameAr: 'طرق وعرة' },
    { id: 'other-motorcycle', name: 'Other', nameAr: 'أخرى' },
    { id: 'scooter', name: 'Scooter', nameAr: 'سكوتر' },
    { id: 'sport-bike', name: 'Sport Bike', nameAr: 'دراجة رياضية' },
    { id: 'standard-commuter', name: 'Standard / Commuter', nameAr: 'قياسي / تنقل' },
    { id: 'trailer-motorcycle', name: 'Trailer', nameAr: 'مقطورة' },
    { id: 'trike', name: 'Trike', nameAr: 'ثلاثية العجلات' },
  ],
  'auto-accessories': [
    { id: 'apparel-merchandise', name: 'Apparel, Merchandise & Accessories', nameAr: 'ملابس وإكسسوارات' },
    { id: 'automotive-tools', name: 'Automotive Tools', nameAr: 'أدوات سيارات' },
    { id: 'boat-parts', name: 'Boat Parts', nameAr: 'قطع قوارب' },
    { id: 'car-4x4-parts', name: 'Car/4x4 Parts', nameAr: 'قطع سيارات/دفع رباعي' },
    { id: 'motorcycle-parts', name: 'Motorcycle Parts', nameAr: 'قطع دراجات نارية' },
    { id: 'other-vehicle-parts', name: 'Other Vehicle Parts', nameAr: 'قطع مركبات أخرى' },
  ],
  'heavy-vehicles': [
    { id: 'aircrafts', name: 'Aircrafts', nameAr: 'طائرات' },
    { id: 'buses', name: 'Buses', nameAr: 'حافلات' },
    { id: 'cranes', name: 'Cranes', nameAr: 'رافعات' },
    { id: 'forklifts', name: 'Forklifts', nameAr: 'رافعات شوكية' },
    { id: 'other-heavy', name: 'Other Heavy Vehicles', nameAr: 'مركبات ثقيلة أخرى' },
    { id: 'parts-engines', name: 'Parts & Engines', nameAr: 'قطع ومحركات' },
    { id: 'tankers', name: 'Tankers', nameAr: 'صهاريج' },
    { id: 'trailers', name: 'Trailers', nameAr: 'مقطورات' },
    { id: 'trucks', name: 'Trucks', nameAr: 'شاحنات' },
  ],
  'boats': [
    { id: 'motorboats', name: 'Motorboats', nameAr: 'قوارب بمحرك' },
    { id: 'row-paddle-boats', name: 'Row/Paddle Boats', nameAr: 'قوارب تجديف' },
    { id: 'sailboats', name: 'Sailboats', nameAr: 'قوارب شراعية' },
  ],
};

const carBrands = [
  'Abarth', 'Acura', 'AITO', 'Al Damani', 'Alfa Romeo', 'Arcfox', 'Ariel', 
  'Ashok Leyland', 'Aston Martin', 'Audi', 'Aurus', 'Austin-Healey', 'Avatr',
  'BAC', 'Backdraft', 'BAIC', 'BAW', 'Bentley', 'Bentley Onyx', 'Bestune', 
  'Bizzarrini', 'BMW', 'BMW Alpina', 'Borgward', 'Brilliance', 'Bufori', 
  'Bugatti', 'Buick', 'BYD', 'Cadillac', 'Can-am', 'Caterham', 'CEVO', 
  'Changan', 'Chery', 'Chevrolet', 'Chrysler', 'Citroen', 'CMC', 'Cupra', 
  'Dacia', 'Daewoo', 'Daihatsu', 'Datsun', 'DeLorean', 'DFSK', 'Dodge', 
  'Dongfeng', 'DS', 'Exeed', 'FAW', 'Ferrari', 'Fiat', 'Fisker', 'Ford', 
  'Foton', 'GAC', 'Geely', 'Genesis', 'GMC', 'Great Wall', 'Haval', 'Hennessey',
  'Hino', 'Honda', 'Hongqi', 'Hummer', 'Hyundai', 'Infiniti', 'Isuzu', 
  'JAC', 'Jaguar', 'Jeep', 'Jetour', 'JMC', 'Karma', 'Kia', 'King Long',
  'Koenigsegg', 'KTM', 'Lada', 'Lamborghini', 'Lancia', 'Land Rover', 
  'Leapmotor', 'Lexus', 'Lincoln', 'Lotus', 'Lucid', 'Lynk & Co', 
  'Mahindra', 'Maserati', 'Maxus', 'Maybach', 'Mazda', 'McLaren', 
  'Mercedes-Benz', 'Mercury', 'MG', 'Mini', 'Mitsubishi', 'Morgan', 
  'Neta', 'Nio', 'Nissan', 'Noble', 'Omoda', 'Opel', 'Ora', 'Pagani',
  'Peugeot', 'Polestar', 'Pontiac', 'Porsche', 'Proton', 'Ram', 
  'Renault', 'Rimac', 'Rivian', 'Rolls-Royce', 'Rover', 'Saab', 
  'Seat', 'Skoda', 'Skywell', 'Smart', 'SsangYong', 'Subaru', 'Suzuki', 
  'Tata', 'Tesla', 'Toyota', 'Trumpchi', 'VGV', 'Volkswagen', 'Volvo', 
  'Wuling', 'XPeng', 'Zeekr', 'Zotye', 'ZX Auto'
];

const CategoryNavigation = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { categoryId, subcategoryId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [categoryId, subcategoryId]);

  const getIcon = (slug) => categoryIcons[slug] || Package;

  const getSubcategoryName = (catId, subId) => {
    const cat = categoryData[catId];
    if (!cat) return '';
    const sub = cat.subcategories.find(s => s.id === subId);
    if (!sub) return '';
    return i18n.language === 'ar' ? sub.nameAr : sub.name;
  };

  const getCurrentLevel = () => {
    if (subcategoryId && subSubcategories[subcategoryId]) {
      return {
        level: 3,
        title: getSubcategoryName(categoryId, subcategoryId),
        items: subSubcategories[subcategoryId],
        parentPath: `/browse/${categoryId}`
      };
    }
    if (subcategoryId === 'used-cars' || subcategoryId === 'new-cars' || subcategoryId === 'export-cars' || subcategoryId === 'rental-cars') {
      return {
        level: 2.5,
        title: getSubcategoryName(categoryId, subcategoryId),
        items: carBrands.map(brand => ({ id: brand.toLowerCase().replace(/\s+/g, '-'), name: brand, nameAr: brand })),
        parentPath: `/browse/${categoryId}`,
        isBrands: true
      };
    }
    if (categoryId && categoryData[categoryId]) {
      return {
        level: 2,
        title: i18n.language === 'ar' ? categoryData[categoryId].nameAr : categoryData[categoryId].name,
        items: categoryData[categoryId].subcategories,
        parentPath: '/browse'
      };
    }
    return {
      level: 1,
      title: i18n.language === 'ar' ? 'اختر الفئة' : 'Select Category',
      items: Object.entries(categoryData).map(([id, cat]) => ({
        id,
        name: cat.name,
        nameAr: cat.nameAr,
        hasChildren: cat.subcategories.length > 0,
        subCount: cat.subcategories.length,
      })),
      parentPath: '/'
    };
  };

  const { level, title, items, parentPath, isBrands } = getCurrentLevel();
  const [searchParams] = useSearchParams();
  const hireMode = searchParams.get('mode') === 'hire';

  const filteredItems = items.filter(item => {
    const name = i18n.language === 'ar' ? (item.nameAr || item.name) : item.name;
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleItemClick = (item) => {
    if (level === 1) {
      const modeParam = hireMode ? '?mode=hire' : '';
      navigate(`/browse/${item.id}${modeParam}`);
      return;
    }
    if (item.hasChildren || (level === 2 && subSubcategories[item.id])) {
      const modeParam = hireMode ? '?mode=hire' : '';
      navigate(`/browse/${categoryId}/${item.id}${modeParam}`);
      return;
    }
    // Hire mode: go to post-ad with category pre-selected
    if (hireMode) {
      navigate(`/post-ad?category=${categoryId}&subcategory=${item.id}`);
      return;
    }
    if (isBrands) {
      navigate(`/search?category=${categoryId}&subcategory=${subcategoryId}&brand=${item.name}`);
      return;
    }
    if (level === 2) {
      navigate(`/search?category=${categoryId}&subcategory=${item.id}`);
      return;
    }
    if (level === 3) {
      navigate(`/search?category=${categoryId}&subcategory=${subcategoryId}&type=${item.id}`);
      return;
    }
  };

  const CatIcon = categoryId ? (categoryIcons[categoryId] || Package) : null;

  return (
    <div className="min-h-screen bg-[#f7f8fa] pb-24" data-testid="category-navigation">
      {/* Dark Header */}
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-5xl mx-auto px-4 pt-4 pb-6 relative z-10">
          {/* Nav row */}
          <div className="flex items-center justify-between mb-5">
            <button 
              onClick={() => navigate(parentPath)} 
              className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <button 
              onClick={() => setShowSearch(!showSearch)}
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${showSearch ? 'bg-[#E31C25]' : 'bg-white/10 hover:bg-white/20'}`}
              data-testid="search-toggle-btn"
            >
              <Search className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Title */}
          <div className="animate-fade-up">
            {CatIcon && level >= 2 && (
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center mb-3">
                <CatIcon className="w-5 h-5 text-[#E31C25]" strokeWidth={2} />
              </div>
            )}
            <h1 className="text-2xl md:text-3xl font-extrabold text-white font-heading tracking-tight">
              {hireMode && categoryId === 'jobs' ? (i18n.language === 'ar' ? 'توظيف - ' : 'Hire Talent - ') : ''}{title}
            </h1>
            {level === 1 && (
              <p className="text-white/50 text-sm mt-1">
                {i18n.language === 'ar' ? 'تصفح جميع الفئات' : 'Browse all categories'}
              </p>
            )}
            {level >= 2 && (
              <p className="text-white/50 text-sm mt-1">
                {filteredItems.length} {i18n.language === 'ar' ? 'خيار' : filteredItems.length === 1 ? 'option' : 'options'}
              </p>
            )}
          </div>

          {/* Search bar */}
          {showSearch && (
            <div className="mt-4 animate-fade-up">
              <div className="flex items-center gap-2.5 px-4 py-3 bg-white rounded-xl shadow-lg">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={i18n.language === 'ar' ? 'بحث...' : 'Search...'}
                  className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder-gray-400"
                  autoFocus
                  data-testid="category-search"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Level 1: Main Category Grid */}
        {level === 1 && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {filteredItems.map((item, index) => {
              const Icon = getIcon(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`animate-scale-in stagger-${Math.min(index + 1, 9)} relative bg-white rounded-2xl p-4 border border-gray-100 hover:border-[#E31C25]/30 hover:shadow-md transition-all duration-200 group text-left`}
                  data-testid={`item-${item.id}`}
                >
                  <div className="w-12 h-12 bg-[#FEE2E2] rounded-2xl flex items-center justify-center mb-3 group-hover:bg-[#E31C25] group-hover:scale-110 transition-all">
                    <Icon className="w-6 h-6 text-[#E31C25] group-hover:text-white transition-colors" strokeWidth={2} />
                  </div>
                  <h3 className="font-bold text-gray-800 text-sm leading-tight mb-1">
                    {i18n.language === 'ar' ? (item.nameAr || item.name) : item.name}
                  </h3>
                  {item.subCount > 0 && (
                    <p className="text-[11px] text-gray-400 font-medium">
                      {item.subCount} {i18n.language === 'ar' ? 'فئة فرعية' : item.subCount === 1 ? 'subcategory' : 'subcategories'}
                    </p>
                  )}
                  <ChevronRight className="absolute top-4 right-3 w-4 h-4 text-gray-200 group-hover:text-[#E31C25] group-hover:translate-x-0.5 transition-all" />
                </button>
              );
            })}
          </div>
        )}

        {/* Level 2+: Subcategory List */}
        {level >= 2 && (
          <div>
            {/* All in Category */}
            <button
              onClick={() => navigate(`/search?category=${categoryId}${subcategoryId ? `&subcategory=${subcategoryId}` : ''}`)}
              className="w-full flex items-center justify-between px-4 py-3.5 mb-2 bg-gradient-to-r from-[#E31C25] to-[#b91420] rounded-2xl shadow-sm group animate-fade-up"
              data-testid="all-in-category"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-semibold text-sm">
                  {i18n.language === 'ar' ? `الكل في ${title}` : `All in ${title}`}
                </span>
              </div>
              <ChevronRight className="w-5 h-5 text-white/70 group-hover:translate-x-0.5 transition-transform" />
            </button>

            {/* Items */}
            <div className="space-y-1.5">
              {filteredItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`animate-fade-up stagger-${Math.min(index + 1, 9)} w-full flex items-center justify-between px-4 py-3.5 bg-white rounded-2xl border border-gray-100 hover:border-[#E31C25]/20 hover:bg-red-50/30 transition-all group`}
                  data-testid={`item-${item.id}`}
                >
                  <span className="text-gray-800 font-medium text-sm">
                    {i18n.language === 'ar' ? (item.nameAr || item.name) : item.name}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#E31C25] group-hover:translate-x-0.5 transition-all" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {filteredItems.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm font-medium">
              {i18n.language === 'ar' ? 'لا توجد نتائج' : 'No results found'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryNavigation;
